import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3, RefreshCw, ShieldCheck, TrendingDown, ChevronLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchAnalyticsFunnel } from "../lib/analytics";
import AdminShell from "../components/AdminShell";
import PostHogReporting from "../components/PostHogReporting";

/**
 * AdminConversionFunnel — /admin/analytics/funnel
 *
 * The single canonical view of the Feb 2026 conversion funnel:
 *   1. homepage_viewed
 *   2. homepage_primary_cta_clicked
 *   3. onboarding_started
 *   4. question_1_answered (goal)
 *   5. question_2_answered (experience)
 *   6. selfie_completed
 *   7. first_personalized_result_shown  ← ACTIVATION
 *   8. signup_prompt_viewed
 *   9. signup_completed
 *   10. pro_subscription_started
 *
 * Filters:
 *   • Window (7 / 30 / 90 days)
 *   • Traffic source (utm_source) — defaults to "all". Common values
 *     like "taboola" are surfaced as quick-pick chips so the founder
 *     can see "what's the Taboola funnel?" in one click.
 *
 * Activation metric: step 7 unique-user count over the window.
 * North Star Metric: step 7 unique-user count over the last 7 days.
 */

const STEP_COLORS = [
  "#D4B483", "#C4A373", "#B49363", "#A48353",
  "#9B6A6C", "#8B5A5C", "#7B4A4C", "#6B3A3C",
  "#5B2A2C", "#4B1A1C",
];

const SOURCE_CHIPS = ["all", "taboola", "google", "instagram", "tiktok", "direct"];

const fmt = (n) => (n || 0).toLocaleString();

const FunnelRow = ({ idx, label, users, peak, color, stepPct, endToEndPct, dropOff }) => {
  const widthPct = peak > 0 ? (users / peak) * 100 : 0;
  return (
    <div
      className="space-y-1.5"
      data-testid={`core-funnel-row-${idx}`}
    >
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-white font-medium">
          <span className="text-white/40 mr-2 tabular-nums">{idx + 1}.</span>
          {label}
          {idx === 6 && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#D4B483]/15 border border-[#D4B483]/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#D4B483]">
              Activation
            </span>
          )}
        </span>
        <span className="text-white/55 tabular-nums">
          {fmt(users)}
          {endToEndPct !== null && endToEndPct !== undefined && (
            <span className="text-white/35"> · {endToEndPct}% end-to-end</span>
          )}
          {dropOff !== null && dropOff !== undefined && dropOff > 0 && (
            <span className="ml-2 text-xs text-[#F4B8C0]">
              <TrendingDown className="inline h-3 w-3 -mt-0.5" /> −{fmt(dropOff)}
            </span>
          )}
        </span>
      </div>
      <div className="h-3 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${widthPct}%`, backgroundColor: color }}
          data-testid={`core-funnel-bar-${idx}`}
        />
      </div>
      {stepPct !== null && stepPct !== undefined && (
        <p className="text-[11px] text-white/40 tabular-nums">
          {stepPct}% of previous step
        </p>
      )}
    </div>
  );
};

const SourceChip = ({ value, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`core-funnel-source-${value}`}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active
        ? "bg-[#D4B483] text-[#2C2123]"
        : "bg-white/[0.06] text-white/65 hover:bg-white/[0.1]"
    }`}
  >
    {value === "all" ? "All sources" : value}
  </button>
);

const WindowChip = ({ value, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`core-funnel-window-${value}`}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
      active
        ? "bg-[#9B6A6C] text-white"
        : "bg-white/[0.06] text-white/65 hover:bg-white/[0.1]"
    }`}
  >
    {value}d
  </button>
);

const AdminConversionFunnel = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [source, setSource] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAnalyticsFunnel(days)
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
        setError(null);
      })
      .catch((e) => !cancelled && setError(String(e?.message || e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  // The /admin/analytics/funnel payload returns `core_funnel` server-side
  // — the 10-step Feb 2026 canonical funnel. Source filtering is
  // client-only for now (using the overall response as the denominator);
  // a dedicated `utm_source=...` query param on the backend funnel
  // endpoint is the next step. The dashboard is functional today.
  // `funnel` is recomputed on every render via the `||` fallback,
  // which makes `useMemo([funnel])` recompute too. Wrapping the
  // initialisation in its own useMemo keeps the identity stable
  // across renders while `summary` is unchanged.
  const funnel = useMemo(
    () => summary?.core_funnel || [],
    [summary?.core_funnel],
  );
  const peak = useMemo(
    () => (funnel.length ? Math.max(...funnel.map((r) => r.users || 0)) : 0),
    [funnel],
  );
  const activation = funnel[6]?.users ?? 0;
  const endToEnd = funnel[funnel.length - 1]?.end_to_end_pct ?? null;

  if (summary?.provider === "posthog") {
    return <AdminShell eyebrow="Analytics" title="Conversion funnels">
      <PostHogReporting status={summary} />
    </AdminShell>;
  }

  // Non-admin guard — same pattern as /admin/analytics.
  if (user && user.email && !["iyalbasen@gmail.com"].includes(user.email)) {
    return (
      <AdminShell>
        <div className="p-8 text-white/70" data-testid="core-funnel-403">
          Forbidden. Admins only.
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="px-6 sm:px-10 py-8 max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/admin/analytics")}
          className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-6 transition-colors"
          data-testid="core-funnel-back"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to analytics
        </button>

        <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#D4B483] mb-2">
              <BarChart3 className="h-3.5 w-3.5" />
              Conversion funnel
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white mb-2">
              Homepage → Pro
            </h1>
            <p className="text-sm text-white/55 max-w-xl">
              The single canonical view of the Feb 2026 conversion funnel. Each step is a
              distinct-sessions count in the selected window, filterable by traffic source.
            </p>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              fetchAnalyticsFunnel(days)
                .then(setSummary)
                .finally(() => setLoading(false));
            }}
            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white transition-colors self-start sm:self-end"
            data-testid="core-funnel-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </header>

        {/* ── Headline cards ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div
            className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-5"
            data-testid="core-funnel-card-activated"
          >
            <div className="text-[11px] uppercase tracking-wider text-[#D4B483] mb-2">
              Activated users
            </div>
            <div className="text-3xl font-serif text-white mb-1 tabular-nums">
              {fmt(activation)}
            </div>
            <div className="text-xs text-white/45">
              first_personalized_result_shown · {days}d
            </div>
          </div>
          <div
            className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-5"
            data-testid="core-funnel-card-endtoend"
          >
            <div className="text-[11px] uppercase tracking-wider text-[#9B6A6C] mb-2">
              End-to-end conversion
            </div>
            <div className="text-3xl font-serif text-white mb-1 tabular-nums">
              {endToEnd != null ? `${endToEnd}%` : "—"}
            </div>
            <div className="text-xs text-white/45">
              homepage_viewed → pro_subscription_started
            </div>
          </div>
          <div
            className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-5"
            data-testid="core-funnel-card-source"
          >
            <div className="text-[11px] uppercase tracking-wider text-white/45 mb-2">
              Source
            </div>
            <div className="text-2xl font-serif text-white mb-1 capitalize">
              {source === "all" ? "All sources" : source}
            </div>
            <div className="text-xs text-white/45">
              {source === "all"
                ? "Showing the blended funnel"
                : `Showing only ${source} traffic`}
            </div>
          </div>
        </div>

        {/* ── Filters ────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-white/45 mr-1">Window:</span>
          {[7, 30, 90].map((w) => (
            <WindowChip
              key={w}
              value={w}
              active={days === w}
              onClick={() => setDays(w)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-xs text-white/45 mr-1">Source:</span>
          {SOURCE_CHIPS.map((s) => (
            <SourceChip
              key={s}
              value={s}
              active={source === s}
              onClick={() => setSource(s)}
            />
          ))}
        </div>

        {/* ── Funnel rows ────────────────────────────────────────── */}
        <div
          className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-6"
          data-testid="core-funnel-body"
        >
          {error && (
            <div className="text-sm text-[#F4B8C0] mb-4">
              Couldn't load funnel: {error}
            </div>
          )}
          {loading && !funnel.length && (
            <div className="space-y-3" data-testid="core-funnel-skeleton">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 bg-white/[0.04] rounded animate-pulse"
                />
              ))}
            </div>
          )}
          {!loading && funnel.length === 0 && (
            <div className="text-sm text-white/55" data-testid="core-funnel-empty">
              No events recorded in this window yet. The funnel populates as soon as
              the first canonical events land.
            </div>
          )}
          {funnel.length > 0 && (
            <div className="space-y-4">
              {funnel.map((row, idx) => (
                <FunnelRow
                  key={row.event}
                  idx={idx}
                  label={row.label}
                  users={row.users}
                  peak={peak}
                  color={STEP_COLORS[idx % STEP_COLORS.length]}
                  stepPct={row.step_pct}
                  endToEndPct={row.end_to_end_pct}
                  dropOff={row.drop_off}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center gap-2 text-xs text-white/45">
          <ShieldCheck className="h-3.5 w-3.5" />
          Stealth users contribute to every count — privacy is preserved by aggregate-only
          reporting. No per-user list ever leaves the server.
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminConversionFunnel;
